import axios from 'axios';
import { setup } from 'axios-cache-adapter';
import {routes} from '../router/routes';
import {toast} from 'react-toastify';
import toastSettings from '../services/toastSettings';
import ErrorHelper from '../helpers/ErrorHelper';

export class AxiosAdapter {
	constructor() {
		this.endpoint = process.env.REACT_APP_API_ENDPOINT;

		this.instance = setup({
			baseURL: this.endpoint,
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json;charset=UTF-8',
			},
			cache: {
				maxAge: 24 * 60 * 60 * 1000,
			}
		});

		this.cancelError = {
			request: {
				request: {
					status: 429,
					data: {
						message: 'Timed out for 1 minute',
						errors: {},
					},
				},
				message: 'Timed out for 1 minute',
			},
			response: {
				response: {
					status: 429,
					data: {
						message: 'Timed out for 1 minute',
						errors: {},
					},
				},
				message: 'Timed out for 1 minute',
			},
		};

		this.source = undefined;
		this.setCancelTimeout = null;
		this.shouldStopAllRequests = false;

		this.handleCancelToken();

		this.checkForToken();

		this.instance.interceptors.request.use(
			this.handleRequestInterceptorSuccess,
			this.handleRequestInterceptorRejection
		);

		this.instance.interceptors.response.use(
			this.handleResponseInterceptorSuccess,
			this.handleResponseInterceptorRejection
		);

		const urlParams = new URLSearchParams(window.location.search);
		const token = urlParams.get('token');
		if (token !== null) {
			localStorage.setItem('token', token);
			this.checkForToken();
		}
	}

	/**
     * Handles GET request
     *
     * @param {string} url
     * @param {object} config
     * @returns {*}
     */
	get(url, config) {
		return this.instance.get(url, config);
	}

	/**
     * Handles POST request
     *
     * @param {string} url
     * @param {*} data
     * @param {object} config
     * @returns {*}
     */
	post(url, data, config) {
		return this.instance.post(url, data, config);
	}

	/**
     * Handles PUT request
     *
     * @param {string} url
     * @param {*} data
     * @param {object} config
     * @returns {*}
     */
	put(url, data, config) {
		return this.instance.put(url, data, config);
	}

	/**
     * Handles PATCH request
     *
     * @param {string} url
     * @param {*} data
     * @param {object} config
     * @returns {*}
     */
	patch(url, data, config) {
		return this.instance.patch(url, data, config);
	}

	/**
     * Handles DELETE request
     *
     * @param {string} url
     * @param {object} config
     * @returns {*}
     */
	delete(url, config) {
		return this.instance.delete(url, config);
	}

	/**
     * Sets or removes token from the headers
     *
     * @param token
     */
	setToken(token) {
		if (token !== null) {
			this.instance.defaults.headers.common = {
				Authorization: `Bearer ${token}`,
			};
		} else {
			this.instance.defaults.headers.common = {};
			localStorage.removeItem('token');
		}
	}

	/**
     * Checks if the token is in local storage and sets it
     *
     * @param {string} passedToken
     */
	checkForToken(passedToken) {
		const token = passedToken || localStorage.getItem('token');
		if (token) {
			this.setToken(token);
		}
	}

	/**
     * sets the cancel token to the instances of axios and source prop, responsible for cancle func
     *
     */
	handleCancelToken() {
		this.instance.CancelToken = axios.CancelToken;
		this.instance.isCancel = axios.isCancel;

		const CancelToken = this.instance.CancelToken;

		if (typeof CancelToken !== 'undefined') {
			this.source = CancelToken.source();
		}
	}

	/**
     * Processes the cancel of the request and puts it on the timeout
     *
     * @param {object} config
     * @param {number} timeout
     * @returns {*}
     */
	processCancelRequest(config, timeout) {
		this.source.cancel();
		if (this.setCancelTimeout === null) {
			this.setCancelTimeout = setTimeout(() => {
				this.shouldStopAllRequests = false;
				this.setCancelTimeout = null;
				this.instance.defaults.cancelToken = null;
				return config;
			}, timeout);
			return config;
		}
		return config;
	}

	handleRequestInterceptorSuccess = config => {
    	if (this.shouldStopAllRequests) {
    		this.processCancelRequest(config, 60000);
    	}

    	return config;
	};

	handleResponseInterceptorSuccess = response => {
    	return response;
	};

	handleRequestInterceptorRejection = error => {
    	if (axios.isCancel(error)) {
    		return Promise.reject(this.cancelError.request);
    	} else {
    		return Promise.reject(error);
    	}
	};

	handleResponseInterceptorRejection = error => {
    	if (Object.hasOwnProperty.call(error, 'response')) {
    		switch (error.response?.status) {
    		case 429:
    			this.instance.defaults.cancelToken = this.source.token;
    			toast.error(ErrorHelper.errorsFactory(error), toastSettings);
    			break;
    		case 401:
    			this.setToken(null);
    			window.location.href = routes.LOGIN;
    			break;
    		case 403:
    			toast.error(ErrorHelper.errorsFactory(error), toastSettings);
    			break;
    		case 405:
    			toast.error(ErrorHelper.errorsFactory(error), toastSettings);
    			break;
    		case 422:
    			toast.error(ErrorHelper.errorsFactory(error), toastSettings);
    			break;
    		case 500:
    			toast.error(ErrorHelper.errorsFactory(error), toastSettings);
    			break;
    		default:
    			toast.error('Error: Undefined error', toastSettings);
    		}
    	}

    	if (axios.isCancel(error)) {
    		return Promise.reject(this.cancelError.response);
    	} else {
    		return Promise.reject(error);
    	}
	};
}
